import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { useHLSVideo } from "../hooks/useHLSVideo";
import CameraSelectionStep from "./CameraSelectionStep";
import TimeSelectionStep from "./TimeSelectionStep";
import VideoPlayerStep from "./VideoPlayerStep";
import { useAuth } from "../contexts/AuthContext";
import { Clock } from "lucide-react";

const RTSPUrlGenerator = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  
  // Check if user has playback permission
  if (!hasPermission('cameras', 'playback')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md mx-4 text-center">
          <Clock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You don't have permission to access RTSP URL generation. Please contact your administrator to request this permission.
          </p>
        </div>
      </div>
    );
  }

  const [searchParams] = useSearchParams();
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [playbackUrl, setPlaybackUrl] = useState("");
  const [hlsUrl, setHlsUrl] = useState("");
  const [rtspUrl, setRtspUrl] = useState("");
  const [playbackId, setPlaybackId] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingCameras, setLoadingCameras] = useState(true);
  const [step, setStep] = useState(0); // Start at 0 to show loader first
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackError, setPlaybackError] = useState("");
  const [conversionStatus, setConversionStatus] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"
  const [initializing, setInitializing] = useState(true); // Track initialization state
  
  // New state variables for search and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCameras, setTotalCameras] = useState(0);
  const [camerasPerPage] = useState(12); // Show 12 cameras per page for grid view

  // Use custom HLS hook
  const { videoRef, retryHLS, isHLSLoading, retryAttempt, viewerCount } = useHLSVideo(hlsUrl, selectedCamera?._id);

  // Helper function to parse local time strings
  const parseLocalTime = (timeStr) => {
    const [datePart, timePart] = timeStr.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  };

  // Fetch available cameras
  useEffect(() => {
    if (initializing) {
      initializeComponent();
    } else {
      fetchCameras(1, searchTerm);
    }
  }, [searchTerm, searchParams]);

  // Handle URL parameter changes after initialization
  useEffect(() => {
    if (!initializing) {
      const cameraId = searchParams.get('cameraId');
      if (cameraId && (!selectedCamera || selectedCamera._id !== cameraId)) {
        console.log('🔄 RTSPUrlGenerator: URL changed, loading new camera:', cameraId);
        loadSpecificCamera(cameraId);
      }
    }
  }, [searchParams, initializing, selectedCamera]);

  // Load a specific camera by ID
  const loadSpecificCamera = async (cameraId, keepLoadingOnFail = false) => {
    try {
      setLoadingCameras(true);
      setError("");
      console.log('📡 RTSPUrlGenerator: Loading specific camera:', cameraId);
      
      const cameraResponse = await api.get(`/cameras/${cameraId}`);
      
      if (cameraResponse.data.success && cameraResponse.data.data) {
        console.log('✅ RTSPUrlGenerator: Successfully loaded camera:', cameraResponse.data.data.name);
        setSelectedCamera(cameraResponse.data.data);
        setError(""); // Clear any previous errors on success
        setStep(2); // Go to time selection
        return true; // Success
      } else {
        console.warn('⚠️ RTSPUrlGenerator: Camera API returned unsuccessful response:', cameraResponse.data);
        if (!keepLoadingOnFail) {
          setError('Camera not found in response');
          setStep(2); // Still go to time selection but with error
        }
        return false; // Failed
      }
    } catch (error) {
      console.error('❌ RTSPUrlGenerator: Error loading specific camera:', error);
      if (!keepLoadingOnFail) {
        if (error.response?.status === 403) {
          setError(`Access denied: You don't have permission to view this camera.`);
        } else if (error.response?.status === 404) {
          setError(`Camera not found: The camera may have been deleted or the link is invalid.`);
        } else {
          setError(`Unable to load camera: ${error.response?.data?.message || error.message}`);
        }
        setStep(2); // Always go to time selection even on error
      }
      return false; // Failed
    } finally {
      if (!keepLoadingOnFail) {
        setLoadingCameras(false);
      }
    }
  };

  // Fetch cameras list and try to find the specific camera
  const fetchCamerasAndFindCamera = async (targetCameraId) => {
    try {
      setLoadingCameras(true);
      console.log('📡 RTSPUrlGenerator: Fetching camera list to find camera:', targetCameraId);
      
      const response = await api.get('/cameras/mine');
      
      if (response.data.success && response.data.data.cameras) {
        const foundCamera = response.data.data.cameras.find(camera => camera._id === targetCameraId);
        
        if (foundCamera) {
          console.log('✅ RTSPUrlGenerator: Found camera in list:', foundCamera.name);
          setSelectedCamera(foundCamera);
          setError(""); // Clear any previous errors
        } else {
          console.warn('⚠️ RTSPUrlGenerator: Camera not found in user camera list');
          
          // Set first available camera as fallback and clear error since we have a camera
          if (response.data.data.cameras.length > 0) {
            setSelectedCamera(response.data.data.cameras[0]);
            setError(""); // Clear error since we found a fallback camera
            console.log('🔄 RTSPUrlGenerator: Using first available camera as fallback:', response.data.data.cameras[0].name);
          } else {
            setError('No accessible cameras available');
          }
        }
        
        setCameras(response.data.data.cameras);
      } else {
        console.warn('⚠️ RTSPUrlGenerator: Camera list API returned unsuccessful response:', response.data);
        setError('Failed to load camera list');
      }
    } catch (error) {
      console.error('❌ RTSPUrlGenerator: Error fetching camera list:', error);
      setError('Failed to load cameras: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoadingCameras(false);
    }
  };

  // Initialize component - handle cameraId from URL first
  const initializeComponent = async () => {
    const cameraId = searchParams.get('cameraId');
    console.log('🎯 RTSPUrlGenerator: Initializing with cameraId:', cameraId);
    
    if (cameraId) {
      // Try to load specific camera by ID first
      const success = await loadSpecificCamera(cameraId);
      setInitializing(false);
      
      // If specific camera loading failed, try to load camera list and find it there
      if (!success) {
        console.log('🔄 RTSPUrlGenerator: Specific camera failed, trying camera list fallback');
        setError(""); // Clear any error from the first attempt
        await fetchCamerasAndFindCamera(cameraId);
      } else {
        // Still fetch cameras list for potential camera switching
        fetchCameras(1, searchTerm);
      }
      return;
    }
    
    // Fallback to normal camera list fetch if no cameraId
    console.log('🔄 RTSPUrlGenerator: No cameraId, loading camera list');
    fetchCameras(1, searchTerm);
  };

  const fetchCameras = async (page = 1, search = "") => {
    try {
      setLoadingCameras(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: camerasPerPage.toString(),
      });
      
      if (search) {
        params.append("search", search);
      }
      
      const response = await api.get(`/cameras/mine?${params.toString()}`);
      setCameras(response.data.data.cameras);
      setTotalPages(response.data.data.pagination.pages);
      setTotalCameras(response.data.data.pagination.total);
      setCurrentPage(page);
      
      // Only handle initial camera selection if we're still initializing, no camera is selected, and no cameraId in URL
      const cameraId = searchParams.get('cameraId');
      if (initializing && !selectedCamera && !cameraId && response.data.data.cameras.length > 0) {
        setSelectedCamera(response.data.data.cameras[0]);
        setStep(2); // Go directly to time selection
        setInitializing(false);
      } else if (initializing && !selectedCamera && !cameraId) {
        // No cameras available, show error
        setError("No cameras available");
        setStep(2); // Still go to time selection but with error
        setInitializing(false);
      } else if (initializing) {
        // If we have a cameraId but reach here, it means the specific camera fetch failed
        // Complete initialization anyway
        setInitializing(false);
      }
    } catch (error) {
      console.error("Error fetching cameras:", error);
      setError("Failed to load cameras");
      // Even on error, stop initializing and show time selection
      if (initializing) {
        setStep(2);
        setInitializing(false);
      }
    } finally {
      setLoadingCameras(false);
    }
  };

  // Convert RTSP to HLS using backend API
  const convertRTSPToHLS = async () => {
    try {
      setIsGenerating(true);
      setError("");
      setCopied(false);
      setPlaybackError("");
      setConversionStatus("Converting video...");

      if (!selectedCamera) {
        throw new Error("Please select a camera");
      }

      if (!startTime || !endTime) {
        throw new Error("Please select both start and end times");
      }

      console.log('🎯 convertRTSPToHLS - Using startTime:', startTime);
      console.log('🎯 convertRTSPToHLS - Using endTime:', endTime);
      
      const startDate = parseLocalTime(startTime);
      const endDate = parseLocalTime(endTime);

      if (startDate >= endDate) {
        throw new Error("End time must be after start time");
      }

      // Convert local times to UTC for server
      // The times are already in local timezone, so we need to convert them properly
      const startDateLocal = parseLocalTime(startTime);
      const endDateLocal = parseLocalTime(endTime);
      
      // Convert to UTC by using the local timezone offset
      const startTimeUTC = new Date(startDateLocal.getTime() - (startDateLocal.getTimezoneOffset() * 60000));
      const endTimeUTC = new Date(endDateLocal.getTime() - (endDateLocal.getTimezoneOffset() * 60000));
      
      const startTimeUTCString = startTimeUTC.toISOString().slice(0, 16);
      const endTimeUTCString = endTimeUTC.toISOString().slice(0, 16);
      
      const response = await api.post(
        `/cameras/${selectedCamera._id}/rtsp-to-hls`,
        {
          startTime: startTimeUTCString,
          endTime: endTimeUTCString,
        }
      );

      if (response.data.success) {
        const { hlsUrl, rtspUrl, playbackId } = response.data.data;
        
        setHlsUrl(hlsUrl);
        setRtspUrl(rtspUrl);
        setPlaybackId(playbackId);
        setPlaybackUrl(rtspUrl);
        setConversionStatus("Video ready!");
        setStep(3);
      } else {
        console.error('RTSP to HLS conversion failed:', response.data);
        throw new Error(
          response.data.message || "Failed to convert video"
        );
      }
    } catch (error) {
      console.error("Error converting RTSP to HLS:", error);
      
      setError(error.response?.data?.message || error.message);
      setConversionStatus("Conversion failed");
    } finally {
      setIsGenerating(false);
    }
  };

  // Copy URL to clipboard
  const copyToClipboard = async () => {
    try {
      const urlToCopy = rtspUrl || playbackUrl;
      await navigator.clipboard.writeText(urlToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  // Handle camera selection
  const handleCameraSelect = (camera) => {
    setSelectedCamera(camera);
    setStep(2);
    setError(""); // Clear any previous errors
    setPlaybackUrl("");
    setHlsUrl("");
    setRtspUrl("");
    setPlaybackError("");
    setConversionStatus("");
  };

  // Handle time selection
  const handleTimeSelection = () => {
    console.log('🎯 Manual time selection - Start time:', startTime);
    console.log('🎯 Manual time selection - End time:', endTime);
    console.log('🎯 Manual time selection - Selected camera:', selectedCamera?.name);
    
    if (!startTime || !endTime) {
      setError("Please select both start and end times");
      return;
    }

    const startDate = parseLocalTime(startTime);
    const endDate = parseLocalTime(endTime);

    if (startDate >= endDate) {
      setError("End time must be after start time");
      return;
    }

    console.log('🎯 Manual time selection - Calling convertRTSPToHLS');
    convertRTSPToHLS();
  };

  // Handle timeline time range changes
  const handleTimelineTimeRangeChange = (newStartTime, newEndTime) => {
    console.log('🎯 RTSPUrlGenerator: Timeline time range changed - Start:', newStartTime, 'End:', newEndTime);
    console.log('🎯 RTSPUrlGenerator: Selected camera:', selectedCamera?.name);
    
    setStartTime(newStartTime);
    setEndTime(newEndTime);
    setError(""); // Clear any previous errors
    
    // Automatically regenerate the RTSP URL with new times using the same logic as convertRTSPToHLS
    if (selectedCamera && newStartTime && newEndTime) {
      console.log('🎯 RTSPUrlGenerator: Calling convertRTSPToHLSWithTimes');
      convertRTSPToHLSWithTimes(newStartTime, newEndTime);
    } else {
      console.warn('⚠️ RTSPUrlGenerator: Missing requirements - Camera:', !!selectedCamera, 'StartTime:', !!newStartTime, 'EndTime:', !!newEndTime);
    }
  };

  // Convert RTSP to HLS with specific times - uses exact same logic as convertRTSPToHLS
  const convertRTSPToHLSWithTimes = async (startTimeStr, endTimeStr) => {
    try {
      setIsGenerating(true);
      setError("");
      setCopied(false);
      setPlaybackError("");
      setConversionStatus("Converting video...");

      if (!selectedCamera) {
        throw new Error("Please select a camera");
      }

      if (!startTimeStr || !endTimeStr) {
        throw new Error("Please select both start and end times");
      }

      const startDate = parseLocalTime(startTimeStr);
      const endDate = parseLocalTime(endTimeStr);

      if (startDate >= endDate) {
        throw new Error("End time must be after start time");
      }

      // Convert local times to UTC for server - exact same logic as convertRTSPToHLS
      const startDateLocal = parseLocalTime(startTimeStr);
      const endDateLocal = parseLocalTime(endTimeStr);
      
      // Convert to UTC by using the local timezone offset
      const startTimeUTC = new Date(startDateLocal.getTime() - (startDateLocal.getTimezoneOffset() * 60000));
      const endTimeUTC = new Date(endDateLocal.getTime() - (endDateLocal.getTimezoneOffset() * 60000));
      
      const startTimeUTCString = startTimeUTC.toISOString().slice(0, 16);
      const endTimeUTCString = endTimeUTC.toISOString().slice(0, 16);
      
      const response = await api.post(
        `/cameras/${selectedCamera._id}/rtsp-to-hls`,
        {
          startTime: startTimeUTCString,
          endTime: endTimeUTCString,
        }
      );

      if (response.data.success) {
        const { hlsUrl, rtspUrl, playbackId } = response.data.data;
        
        console.log('🎯 RTSPUrlGenerator: Video conversion successful');
        console.log('🎯 HLS URL:', hlsUrl);
        console.log('🎯 RTSP URL:', rtspUrl);
        console.log('🎯 Playback ID:', playbackId);
        
        console.log('🎯 Setting new HLS URL:', hlsUrl);
        setHlsUrl(hlsUrl);
        setRtspUrl(rtspUrl);
        setPlaybackId(playbackId);
        setPlaybackUrl(rtspUrl);
        setConversionStatus("Video ready!");
        // Don't set step here since we're already in step 3 (video player)
      } else {
        console.error('RTSP to HLS conversion failed:', response.data);
        throw new Error(
          response.data.message || "Failed to convert video"
        );
      }
    } catch (error) {
      console.error("Error converting RTSP to HLS:", error);
      
      setError(error.response?.data?.message || error.message);
      setConversionStatus("Conversion failed");
    } finally {
      setIsGenerating(false);
    }
  };

  // Convert UTC to IST (UTC+5:30)
  const convertUTCToIST = (date) => {
    const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds
    return new Date(date.getTime() + istOffset);
  };

  // Convert IST to UTC for server
  const convertISTToUTC = (date) => {
    const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds
    return new Date(date.getTime() - istOffset);
  };

  // Quick time presets
  const setTimePreset = (minutes) => {
    // Get current time in local timezone
    const now = new Date();
    const start = new Date(now.getTime() - minutes * 60 * 1000);
    
    // Format times in local timezone for display and storage
    const formatLocalTime = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    const startTimeStr = formatLocalTime(start);
    const endTimeStr = formatLocalTime(now);
    
    setStartTime(startTimeStr);
    setEndTime(endTimeStr);
    
    console.log('Time preset set:', {
      minutes,
      startTime: startTimeStr,
      endTime: endTimeStr,
      startDate: start.toLocaleString('en-IN'),
      endDate: now.toLocaleString('en-IN'),
      currentTime: now.toLocaleString('en-IN')
    });
  };

  // Reset to start
  const handleReset = () => {
    setSelectedCamera(null);
    setStartTime("");
    setEndTime("");
    setPlaybackUrl("");
    setHlsUrl("");
    setRtspUrl("");
    setPlaybackId("");
    setError("");
    setPlaybackError("");
    setConversionStatus("");
    setIsPlaying(false);
    setIsMuted(false);
    setStep(2); // Go back to time selection
    setSearchTerm("");
    setCurrentPage(1);
    setInitializing(false); // Don't show loader again on reset
    // Select first camera again
    if (cameras.length > 0) {
      setSelectedCamera(cameras[0]);
    }
  };

  // Go back to previous step
  const handleBack = () => {
    if (step === 2) {
      // From time selection, go back to camera monitoring
      navigate('/camera-monitoring');
    } else if (step === 3) {
      setStep(2);
      setPlaybackUrl("");
      setHlsUrl("");
      setRtspUrl("");
      setPlaybackError("");
    }
  };

  // Video player controls
  const handlePlayPause = (playing) => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
      setIsPlaying(playing);
    }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const handleRetry = () => {
    setPlaybackError("");
    retryHLS();
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle pagination
  const handlePageChange = (page) => {
    fetchCameras(page, searchTerm);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-cyan-50 to-indigo-100">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
        {/* Step 0: Initial Loader */}
        {step === 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-400 mb-6"></div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Preparing Time Range Selection
              </h2>
              <p className="text-gray-600 text-center">
                Loading system components and preparing the time range selection interface...
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Time Selection */}
        {step === 2 && (
          <TimeSelectionStep
            selectedCamera={selectedCamera}
            loadingCameras={loadingCameras}
            startTime={startTime}
            endTime={endTime}
            error={error}
            isGenerating={isGenerating}
            conversionStatus={conversionStatus}
            onBack={handleBack}
            onStartTimeChange={setStartTime}
            onEndTimeChange={setEndTime}
            onTimePreset={setTimePreset}
            onTimeSelection={handleTimeSelection}
          />
        )}

        {/* Step 3: Video Player */}
        {step === 3 && hlsUrl && (
          <>
            <VideoPlayerStep
              onBack={handleBack}
              isPlaying={isPlaying}
              isMuted={isMuted}
              playbackError={playbackError}
              onPlayPause={handlePlayPause}
              onMuteToggle={handleMuteToggle}
              onFullscreen={handleFullscreen}
              onRetry={handleRetry}
              onStartTimeChange={setStartTime}
              onEndTimeChange={setEndTime}
              onTimeRangeChange={handleTimelineTimeRangeChange}
              videoRef={videoRef}
              isHLSLoading={isHLSLoading}
              hlsRetryAttempt={retryAttempt}
            />



          </>
        )}
      </div>
    </div>
  );
};

export default RTSPUrlGenerator;
